/* Il listino: quanto vale un'ora, e per chi.
 *
 * **Il listino si allunga, non si riscrive.** Un aumento e' una tariffa nuova con una data nuova, e
 * quella vecchia resta a valere per il lavoro fatto prima: riscrivere il prezzo di marzo a
 * settembre cambierebbe il maturato di ore gia' lavorate, cioe' un numero plausibile e falso. Le
 * uniche due eccezioni servono a rimediare a un errore appena fatto: ripetere la stessa tariffa per
 * lo stesso giorno ne corregge la cifra, e una riga inserita per sbaglio si toglie.
 *
 * **Dal 16 ottobre 2026 la tiene chi fattura**, non piu' solo il titolare: una tariffa che cambia
 * non deve richiedere una telefonata ai genitori. Il permesso largo regge perche' ogni salvataggio
 * passa da un'anteprima (quante pratiche cambiano di valore, da quanto a quanto), una cifra fuori
 * scala chiede una conferma esplicita, e ogni modifica lascia una riga che il titolare rilegge in
 * Gestione. Le ore gia' fatturate non cambiano mai: la loro tariffa e' congelata.
 *
 * **La regola di scelta e' una sola, ed e' scritta in pagina** perche' chi compila deve poterla
 * prevedere: fra le tariffe che valgono per un'ora vince la piu' specifica, poi la piu' recente, e
 * a parita' il programma si ferma invece di sceglierne una a caso (e rifiuta di salvare una tariffa
 * che produrrebbe quella parita').
 *
 * **La tariffa per persona non si puo' ancora scrivere da qui.** Il listino la conosce, ma serve
 * l'identificativo dell'utente, e l'elenco degli utenti e' del solo titolare. Scriverlo a mano in un
 * campo di testo vorrebbe dire una tariffa che non combacia mai con nessuno, senza che niente lo dica.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ErroreApi, anagrafica, listino } from "../api";
import { useCarica } from "../carica";
import {
  TIPI_PRATICA,
  conta,
  dataCompleta,
  euro,
  importoDaScritto,
  nomeTipo,
  oggi,
  type AnteprimaTariffa,
  type Cliente,
  type Tariffa,
} from "../dominio";

export function Tariffe() {
  const tariffe = useCarica<Tariffa[]>(() => listino.tariffe(), []);
  const clienti = useCarica<Cliente[]>(() => anagrafica.clienti(), []);

  const nomeCliente = useMemo(() => {
    const mappa = new Map<string, string>();
    for (const c of clienti.dati ?? []) mappa.set(c.identificativo, c.denominazione);
    return (id: string) => mappa.get(id) ?? "cliente non trovato";
  }, [clienti.dati]);

  const errore = tariffe.errore ?? clienti.errore;

  return (
    <>

      <p>
        Quanto vale un'ora di lavoro. Una tariffa può valere per tutti, per un
        tipo di lavoro, per un cliente, o per una combinazione. Quando un'ora
        trova più tariffe, <strong>vince la più specifica</strong>, poi la più
        recente. Se due tariffe sono ugualmente specifiche e partono lo stesso
        giorno, il programma si ferma e lo dice, invece di sceglierne una a caso.
      </p>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      {tariffe.dati === undefined || clienti.dati === undefined ? (
        <p className="attesa">Un momento.</p>
      ) : (
        <>
          <NuovaTariffa clienti={clienti.dati} dopo={tariffe.ricarica} />
          <Elenco tariffe={tariffe.dati} nomeCliente={nomeCliente} dopo={tariffe.ricarica} />
        </>
      )}
    </>
  );
}

/** A chi vale una tariffa, in parole. "Tutti" non e' un'etichetta di comodo:
 * e' la tariffa che prende ogni ora che non ne trova una piu' precisa, ed e'
 * la prima da scrivere. */
function aChiVale(t: Tariffa, nomeCliente: (id: string) => string): string {
  const pezzi: string[] = [];
  if (t.tipo_pratica) pezzi.push(nomeTipo(t.tipo_pratica));
  if (t.cliente_id) pezzi.push(nomeCliente(t.cliente_id));
  if (t.persona) pezzi.push("una persona");
  return pezzi.length === 0 ? "Tutti" : pezzi.join(", ");
}

/** Le tariffe raggruppate per "a chi valgono": dentro ogni gruppo la storia, con quale vale oggi.
 *
 * Il gruppo e' la domanda che si fa chi le guarda ("quanto costa un'ora di bilancio?"), non la data
 * in cui sono state scritte. In ogni gruppo vale oggi la piu' recente gia' partita; le piu' vecchie
 * sono superate (restano, perche' valgono per il lavoro fatto prima) e quelle con una data futura
 * partono piu' avanti. */
function Elenco({
  tariffe,
  nomeCliente,
  dopo,
}: {
  tariffe: Tariffa[];
  nomeCliente: (id: string) => string;
  dopo: () => Promise<void>;
}) {
  if (tariffe.length === 0) {
    return (
      <p className="errore">
        Il listino è vuoto, e finché è così nessuna ora ha un prezzo: il maturato
        le conta a parte come "senza tariffa" e nessun avviso le può fatturare.
        La prima da scrivere è quella che vale per tutti.
      </p>
    );
  }

  const adesso = oggi();
  const gruppi = new Map<string, Tariffa[]>();
  for (const t of tariffe) {
    const chiave = aChiVale(t, nomeCliente);
    gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), t]);
  }
  // "Tutti" per primo, poi per specificita' e nome.
  const ordinati = [...gruppi.entries()]
    .map(([nome, righe]) => ({
      nome,
      righe: [...righe].sort((a, b) => (a.dal < b.dal ? 1 : a.dal > b.dal ? -1 : b.id - a.id)),
    }))
    .sort((a, b) => {
      const sa = a.righe[0]!.specificita;
      const sb = b.righe[0]!.specificita;
      return sa - sb || a.nome.localeCompare(b.nome, "it");
    });
  const generale = tariffe.some((t) => t.specificita === 0);

  return (
    <section className="elenco">
      <h2>{conta(tariffe.length, "tariffa", "tariffe")}</h2>
      {!generale && (
        <p className="riga-avviso">
          Nessuna tariffa vale per tutti: le ore che non rientrano in nessuna di
          queste restano senza prezzo.
        </p>
      )}
      {ordinati.map((g) => {
        const inVigore = g.righe.find((t) => t.dal <= adesso);
        return (
          <div key={g.nome} className="gruppo">
            <div className="gruppo-testa">
              <h3>{g.nome}</h3>
              <span className="cifra">
                {inVigore ? `${euro(inVigore.oraria)} all'ora` : "parte più avanti"}
              </span>
            </div>
            <table className="tabella">
              <tbody>
                {g.righe.map((t) => {
                  const futura = t.dal > adesso;
                  const vale = inVigore !== undefined && t.id === inVigore.id;
                  return (
                    <tr key={t.id} className={vale || futura ? undefined : "tariffa-superata"}>
                      <td>
                        {dataCompleta(t.dal)}
                        {t.nota && <span className="narrativa">{t.nota}</span>}
                      </td>
                      <td className="colonna-stato">
                        {vale ? (
                          <span className="stato-tariffa vale">vale oggi</span>
                        ) : futura ? (
                          <span className="stato-tariffa">parte più avanti</span>
                        ) : (
                          <span className="stato-tariffa">superata</span>
                        )}
                      </td>
                      <td className="cifra colonna-importo">{euro(t.oraria)}</td>
                      <td className="colonna-stato">
                        <TogliTariffa id={t.id} dopo={dopo} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </section>
  );
}

/** Toglie una riga inserita per sbaglio. Chiede prima; e se il server dice che delle ore
 * resterebbero senza prezzo, lo mostra e chiede un'altra conferma. */
function TogliTariffa({ id, dopo }: { id: number; dopo: () => Promise<void> }) {
  const [chiedi, setChiedi] = useState(false);
  const [avviso, setAvviso] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function togli() {
    setErrore(null);
    setInCorso(true);
    try {
      await listino.togli(id, avviso !== null);
      await dopo();
    } catch (e) {
      if (e instanceof ErroreApi && e.stato === 409) setAvviso(e.message);
      else setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a toglierla.");
    } finally {
      setInCorso(false);
    }
  }

  if (!chiedi) {
    return (
      <button type="button" className="tasto tasto-nudo" onClick={() => setChiedi(true)}>
        togli
      </button>
    );
  }
  return (
    <span className="togli-tariffa">
      {avviso ? (
        <span className="errore" role="alert">
          {avviso}
        </span>
      ) : (
        <span className="narrativa">Togliere questa riga?</span>
      )}
      <button type="button" className="tasto tasto-piccolo" disabled={inCorso} onClick={togli}>
        {avviso ? "Togli lo stesso" : "Sì, togli"}
      </button>
      <button
        type="button"
        className="tasto tasto-nudo"
        onClick={() => {
          setChiedi(false);
          setAvviso(null);
          setErrore(null);
        }}
      >
        Lascia
      </button>
      {errore && (
        <span className="errore" role="alert">
          {errore}
        </span>
      )}
    </span>
  );
}

function NuovaTariffa({
  clienti,
  dopo,
}: {
  clienti: Cliente[];
  dopo: () => Promise<void>;
}) {
  const [oraria, setOraria] = useState("");
  const [dal, setDal] = useState(oggi());
  const [tipo, setTipo] = useState("");
  const [cliente, setCliente] = useState("");
  const [nota, setNota] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [anteprima, setAnteprima] = useState<AnteprimaTariffa | null>(null);

  /** Toccare un campo butta via l'anteprima: l'effetto mostrato era di un'altra tariffa. */
  function cambia(imposta: (v: string) => void) {
    return (v: string) => {
      imposta(v);
      setAnteprima(null);
      setFatto(null);
    };
  }

  function corpo(importo: string) {
    return {
      oraria: importo,
      dal,
      tipo_pratica: tipo || null,
      cliente_id: cliente || null,
      persona: null,
      nota,
    };
  }

  /** Primo passo: non si salva niente, si guarda cosa cambierebbe. */
  async function controlla(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setFatto(null);
    const importo = importoDaScritto(oraria);
    if (importo === null) {
      setErrore("La tariffa è un importo, per esempio 85 oppure 85,50.");
      return;
    }
    setInCorso(true);
    try {
      setAnteprima(await listino.anteprima(corpo(importo)));
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a controllarla.");
    } finally {
      setInCorso(false);
    }
  }

  /** Secondo passo: chi arriva qui ha visto l'effetto e, se c'era, l'avviso di una cifra fuori scala. */
  async function salva() {
    const importo = importoDaScritto(oraria);
    if (importo === null || anteprima === null) return;
    setErrore(null);
    setInCorso(true);
    try {
      await listino.crea({ ...corpo(importo), conferma: anteprima.conferma !== null });
      setOraria("");
      setNota("");
      setAnteprima(null);
      setFatto(
        anteprima.corregge
          ? `Cifra corretta: ${euro(importo)} all'ora dal ${dataCompleta(dal)}.`
          : `Tariffa di ${euro(importo)} all'ora aggiunta. Il maturato la usa da subito per le ore dal ${dataCompleta(dal)} in poi.`,
      );
      await dopo();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a salvare.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <form className="modulo riquadro" onSubmit={controlla} noValidate>
      <h2>Tariffa nuova</h2>

      <div className="imputa-riga">
        <div className="campo campo-minuti">
          <label htmlFor="oraria">Euro all'ora</label>
          <input
            id="oraria"
            inputMode="decimal"
            value={oraria}
            required
            placeholder="85,00"
            onChange={(e) => cambia(setOraria)(e.target.value)}
          />
        </div>
        <div className="campo campo-data">
          <label htmlFor="dal">Vale dal</label>
          <input
            id="dal"
            type="date"
            value={dal}
            required
            onChange={(e) => cambia(setDal)(e.target.value)}
          />
        </div>
      </div>

      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="tipo">Solo per un tipo di lavoro</label>
          <select id="tipo" value={tipo} onChange={(e) => cambia(setTipo)(e.target.value)}>
            <option value="">Tutti i tipi</option>
            {TIPI_PRATICA.map((t) => (
              <option key={t.valore} value={t.valore}>
                {t.nome}
              </option>
            ))}
          </select>
        </div>
        <div className="campo campo-largo">
          <label htmlFor="cliente">Solo per un cliente</label>
          <select id="cliente" value={cliente} onChange={(e) => cambia(setCliente)(e.target.value)}>
            <option value="">Tutti i clienti</option>
            {clienti.map((c) => (
              <option key={c.identificativo} value={c.identificativo}>
                {c.denominazione}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="campo">
        <label htmlFor="nota">Nota</label>
        <input
          id="nota"
          value={nota}
          maxLength={300}
          placeholder="per esempio: aumento deciso a gennaio"
          onChange={(e) => cambia(setNota)(e.target.value)}
        />
        <p className="aiuto">
          Un aumento è una tariffa nuova che parte da una data nuova, e quella vecchia
          resta a valere per il lavoro fatto prima. Prima di salvare vedi quanto cambia
          il valore del lavoro non ancora fatturato; le ore già fatturate non cambiano
          mai. Se ripeti la stessa tariffa per lo stesso giorno, ne correggi la cifra.
        </p>
      </div>

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      {fatto && (
        <p className="riga-fatto" role="status">
          {fatto}
        </p>
      )}

      {anteprima === null ? (
        <div>
          <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
            {inCorso ? "Controllo" : "Controlla cosa cambia"}
          </button>
        </div>
      ) : (
        <Effetto anteprima={anteprima} importo={importoDaScritto(oraria)} dal={dal}>
          <button className="tasto tasto-principale" type="button" disabled={inCorso} onClick={salva}>
            {inCorso
              ? "Sto salvando"
              : anteprima.conferma !== null
                ? "Salva lo stesso"
                : anteprima.corregge
                  ? "Correggi la cifra"
                  : "Aggiungi al listino"}
          </button>
          <button type="button" className="tasto tasto-nudo" onClick={() => setAnteprima(null)}>
            Lascia
          </button>
        </Effetto>
      )}
    </form>
  );
}

/** Cosa cambierebbe salvando, in poche righe: la cifra da cui si parte, il lavoro toccato, l'avviso. */
function Effetto({
  anteprima: a,
  importo,
  dal,
  children,
}: {
  anteprima: AnteprimaTariffa;
  importo: string | null;
  dal: string;
  children: ReactNode;
}) {
  const e = a.effetto;
  const differenza = Number(e.differenza);
  return (
    <div className="anteprima-tariffa" role="status">
      <h3>Prima di salvare</h3>
      {a.corregge && a.cifra_attuale !== null && importo !== null && (
        <p>
          Esiste già questa tariffa per il {dataCompleta(dal)}, di {euro(a.cifra_attuale)} all'ora:
          salvando la cifra diventa {euro(importo)}.
        </p>
      )}
      {!a.corregge && a.precedente !== null && (
        <p className="narrativa">Si parte da {euro(a.precedente)} all'ora.</p>
      )}
      {e.pratiche === 0 ? (
        <p>Il valore del lavoro in corso non cambia.</p>
      ) : (
        <p>
          Cambia il valore di {conta(e.pratiche, "pratica", "pratiche")} con ore non ancora fatturate: da{" "}
          <strong>{euro(e.prima)}</strong> a <strong>{euro(e.dopo)}</strong> (
          {differenza > 0 ? "+" : ""}
          {euro(e.differenza)}).
        </p>
      )}
      {e.senza_tariffa_dopo > e.senza_tariffa_prima && (
        <p className="errore" role="alert">
          {conta(e.senza_tariffa_dopo - e.senza_tariffa_prima, "ora resterebbe", "ore resterebbero")} senza
          prezzo.
        </p>
      )}
      {a.conferma !== null && (
        <p className="errore" role="alert">
          Attenzione: {a.conferma} Controlla la cifra prima di salvare.
        </p>
      )}
      <div>{children}</div>
    </div>
  );
}
